import React, { useState, useEffect } from 'react';
import EmailComposer from './EmailComposer';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import { useFolderStore } from '@/store/folderStore';
import { Tag } from 'react-tag-autocomplete';
// import { useEmails } from '@/hooks/useEmails';

// Add sound utility
const playSentSound = () => {
  const audio = new Audio('/sent.mp3');
  audio.play().catch(error => {
    console.error('Error playing sound:', error);
  });
};

interface DraftAttachment {
  filename: string;
  contentType: string;
  size: number;
  content: string;
}

interface DraftData {
  draftId?: string;
  subject: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  body_html: string;
  body_text: string;
  attachments?: DraftAttachment[];
}

interface ComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    type?: 'new' | 'reply' | 'forward' | 'draft';
    draftId?: string;
    subject?: string;
    body?: string;
    attachments?: File[];
    to?: { value: string; label: string }[];
    cc?: { value: string; label: string }[];
    bcc?: { value: string; label: string }[];
  };
}

const ComposeModal: React.FC<ComposeModalProps> = ({ isOpen, onClose, initialData }) => {
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>(initialData?.draftId);
  const { fetchFolders } = useFolderStore();

  // Function to convert File to DraftAttachment
  const fileToAttachment = async (file: File): Promise<DraftAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Content = reader.result?.toString().split(',')[1];
        if (base64Content) {
          resolve({
            filename: file.name,
            contentType: file.type,
            size: file.size,
            content: base64Content
          });
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  // Function to save draft
  const saveDraft = async (data: { 
    to: Tag[],
    cc: Tag[],
    bcc: Tag[],
    subject: string,
    body: string,
    attachments: File[]
  }) => {
    try {
      setIsSaving(true);
      
      // Convert Files to DraftAttachments
      const draftAttachments = await Promise.all(
        data.attachments.map(file => fileToAttachment(file))
      );

      const draftData: DraftData = {
        draftId: draftId,
        subject: data.subject,
        to: data.to.map(tag => tag.value).filter((value): value is string => value !== null),
        cc: data.cc.map(tag => tag.value).filter((value): value is string => value !== null),
        bcc: data.bcc.map(tag => tag.value).filter((value): value is string => value !== null),
        body_html: data.body,
        body_text: data.body.replace(/<[^>]*>/g, ''),
        attachments: draftAttachments
      };

      const response = await api.post('/api/email/draft', draftData);
      
      if (response.data?.draftId) {
        setDraftId(response.data.draftId);
      }

      toast.success('Draft saved successfully');
      onClose(); // Close the modal after successful save
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Send handler for the modal
  const handleClose = async (composerData: { 
    to: Tag[],
    cc: Tag[],
    bcc: Tag[],
    subject: string,
    body: string,
    attachments: File[]
  }) => {
    // Only show confirmation if there's content to save
    const hasContent = composerData.subject || 
                      composerData.body || 
                      composerData.attachments.length > 0 ||
                      composerData.to.length > 0 ||
                      composerData.cc.length > 0 ||
                      composerData.bcc.length > 0;

    if (hasContent) {
      const shouldSave = window.confirm('Do you want to save this email as a draft?');
      if (shouldSave) {
        await saveDraft(composerData);
      }
    }
    onClose();
  };

  const handleSend = async ({ to, cc, bcc, subject, body, attachments }: any) => {
    if (to.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    // Create a loading toast that will persist until the email is sent
    const loadingToastId = toast.loading(
      'Sending email...', { position: 'bottom-center', style: {
        background: "#000",
        color: "#fff",
      } 
    });

    try {
      setIsSending(true);
      onClose(); // Close the modal immediately, but keep the toast

      const formData = new FormData();
      to.forEach((tag: any) => tag.value && formData.append('to', String(tag.value)));
      cc.forEach((tag: any) => tag.value && formData.append('cc', String(tag.value)));
      bcc.forEach((tag: any) => tag.value && formData.append('bcc', String(tag.value)));
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('bodyType', 'html');
      attachments.forEach((file: File) => formData.append('attachments', file));

      const response = await api.post('/api/email/send', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        timeout: 0 // Disable timeout
      });

      if (response.status === 200 && response.data.success) {
        // Play sent sound
        playSentSound();

        // Update the loading toast to success
        toast.success('Email sent successfully', {
          id: loadingToastId,
          duration: 3000 // Show success for 3 seconds
        });
        
        // Update folder counts only
        await fetchFolders();
        
      } else {
        // If the response is not successful but no error was thrown
        throw new Error('Failed to send email: Server returned unsuccessful response');
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      // Update the loading toast to error
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send email', {
        id: loadingToastId,
        duration: 5000 // Show error for 5 seconds
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  // Mobile view
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <EmailComposer
          type={initialData?.type}
          isSending={isSending}
          isSaving={isSaving}
          onSend={handleSend}
          onSave={saveDraft}
          onCancel={(data) => handleClose(data)}
          initialSubject={initialData?.subject}
          initialBody={initialData?.body}
          initialAttachments={initialData?.attachments}
          initialTo={initialData?.to}
          initialCc={initialData?.cc}
          initialBcc={initialData?.bcc}
        />
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(8px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '800px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '600px',
        maxHeight: '90vh',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        animation: 'modalSlideIn 0.3s ease-out'
      }}>
        <EmailComposer
          type={initialData?.type}
          isSending={isSending}
          isSaving={isSaving}
          onSend={handleSend}
          onSave={saveDraft}
          onCancel={(data) => handleClose(data)}
          initialSubject={initialData?.subject}
          initialBody={initialData?.body}
          initialAttachments={initialData?.attachments}
          initialTo={initialData?.to}
          initialCc={initialData?.cc}
          initialBcc={initialData?.bcc}
        />
      </div>
    </div>
  );
};

export default ComposeModal; 